import type { QueryKey } from '@tanstack/react-query'
import type { DirectoryEvent } from '@/modules/directory-feed/lib/directory-signal'
import { makeQueryKeys } from '@/query/query-keys'

/**
 * The nineteen things the directory channel says, closed here and nowhere
 * else.
 *
 * **The set is closed at this layer on purpose.** `directory-signal.ts` parses
 * `type` as a bare string, because a transport that refused to carry a
 * new event type would drop it silently. Here an unmapped type is
 * visible: the mapping below is exhaustive by the compiler, and
 * `web/scripts/check-directory-events.js` reads
 * `data-plane/crates/dataplane/src/realtime/events.rs` and fails when the two
 * lists disagree. The Rust file is the source; this is the copy that has to
 * keep up with it.
 */
export const DIRECTORY_EVENT_TYPES = [
	'node.created',
	'node.moved',
	'node.renamed',
	'node.uploaded',
	'node.upload_opened',
	'node.upload_failed',
	'node.version_restored',
	'node.version_deleted',
	'node.thumbnail_ready',
	'node.trashed',
	'node.restored',
	'node.purged',
	'trash.emptied',
	'share.granted',
	'share.revoked',
	'share.link_changed',
	'access.changed',
	'storage.changed',
	'directory.resync'
] as const

export type DirectoryEventType = (typeof DIRECTORY_EVENT_TYPES)[number]

// The guard narrows the event and not only its `type`, so that the mapping
// below can take an event whose type the compiler knows is in the union.
function isKnown(
	event: DirectoryEvent
): event is DirectoryEvent & { type: DirectoryEventType } {
	return (DIRECTORY_EVENT_TYPES as readonly string[]).includes(event.type)
}

/**
 * What to re-read when one event arrives, as query keys and never as data.
 *
 * **An event says that something changed, not what it now contains.** Writing
 * the new shape into the list would mean deciding here who may see what, and
 * that decision lives in one place in the data plane with a test holding it
 * there. A second copy in the browser would be wrong at the first edge case
 * and nobody would find out. So every event turns into a re-read.
 *
 * **Narrow where the event says enough, wide where it does not.** A creation
 * names the folder it happened in, so one listing is re-read. A move does not:
 * the payload carries the parent *after* the write, and the folder the node
 * left is not in it, so the only honest answer is every listing. The same
 * holds for the two sharing events, which change what is visible rather than
 * what a node contains: a revocation can remove rows from a listing nothing in
 * the payload identifies.
 *
 * A wide key is not a wide refetch. `invalidateQueries` marks matching queries
 * stale and, by default, refetches only the active ones; a folder nobody is
 * looking at is re-read when it comes back on screen.
 *
 * **The search cache is dated by every type but `storage.changed`, which is why it is added here and
 * not inside the branches.** Every event in the set changes a name, a position
 * or whether a node is there at all, and a search result is a row about exactly
 * those three things — so there is no event that leaves the results alone and
 * nothing to decide per type. It is a second root and not a branch of
 * `directory`: no key below that prefix reaches `search`, so before this a
 * colleague's rename left this tab's results holding the old name until the
 * entry aged out, which takes the client's default ten minutes (OPSSTD-8135).
 * The local mutations invalidate it themselves as well, for the reason they
 * invalidate the tree themselves: a write must not depend on the feed being
 * connected to show its own result.
 *
 * `search.all()` is the whole root because the entries are keyed on the term,
 * the scope and the filters, and an event names none of the three — the same
 * "the payload does not say" that makes a move re-read every listing.
 */
export function invalidationsFor(event: DirectoryEvent): QueryKey[] {
	if (!isKnown(event)) {
		return []
	}

	// The server saying «you have lost the thread»: a channel sent more
	// distinct events in a second than it was worth sending, and this one
	// stands in for all of them. It is the `desync` branch of
	// `use-directory-invalidation.ts`, said by the data plane rather than by
	// Centrifugo, and it gets the same answer — both roots, once.
	if (event.type === 'directory.resync') {
		return [makeQueryKeys.directory.all(), makeQueryKeys.search.all()]
	}

	// «Your space changed» (OPSSTD-11215), sent to the person a write charged
	// or credited whether or not they can see the node: the sidebar's storage
	// meter, and nothing about the tree or the search results, which no byte
	// count is part of.
	if (event.type === 'storage.changed') {
		return [makeQueryKeys.users.meStorage()]
	}

	return [makeQueryKeys.search.all(), ...directoryKeysFor(event)]
}

/** The tree's own keys, split out only so the search root above is written once. */
function directoryKeysFor(
	event: DirectoryEvent & { type: DirectoryEventType }
) {
	// `trash.emptied` carries no single node, so it is handled before the
	// switch narrows the rest to the shape every other type actually has. The
	// cast, not a control-flow narrowing on `event.type`, because `type` is a
	// plain `string` one layer down in `DirectoryEvent` and the two do not
	// simplify back into a discriminated union for the compiler to narrow on.
	if (event.type === 'trash.emptied') {
		return trashEmptiedKeys(
			event as DirectoryEvent & { type: 'trash.emptied' }
		)
	}

	// From here on the producer always sends `nodeId` and `parentId` — see
	// `directory-signal.ts`'s own doc comment for why the two are `.optional()`
	// at the schema layer in the first place. One cast says so once instead of
	// an assertion at each of the twelve branches below. `access.changed` is
	// the exception, and its branch reads neither. `directory.resync` never
	// gets here: `invalidationsFor` answers it before calling this.
	const singular = event as DirectoryEvent & {
		type: Exclude<
			DirectoryEventType,
			'trash.emptied' | 'directory.resync' | 'storage.changed'
		>
		nodeId: string
		parentId: string | null
	}

	switch (singular.type) {
		// The node appeared in, or changed inside, one known folder.
		case 'node.created':
			return singular.parentId === null
				? [makeQueryKeys.directory.all()]
				: [makeQueryKeys.directory.childrenAnySort(singular.parentId)]

		// The three that move a transfer in or out of "somebody is sending
		// into this folder", and therefore re-read two things rather than one.
		//
		// **The second key is not the listing.** `folder-uploads` is its own
		// read — who *besides me* is uploading here — and the badge on the
		// breadcrumb mounts it even where no grid is on screen, so an
		// invalidation of the children alone left it exactly as stale as it was
		// before the feed existed. That was the bug: the indicator was the one
		// thing in the file manager that still needed a reload.
		//
		// `node.uploaded` is in this group as well as being the event that says
		// a file is live: a completed transfer leaves the set the same way an
		// abandoned one does, and it is the only one of the three that also
		// makes the new row appear. It also names new content, and a thumbnail
		// is the picture of one version: a card still holding the previous
		// version's `ready` answer would go on drawing the old picture until its
		// signed URL expired. The re-read answers for the new version instead.
		//
		// A replace is a new version too, so the file's own read (its size and
		// who last changed it) and its history are stale with the thumbnail.
		// Without these two a details panel open on the file in another tab, or
		// on somebody else's screen, went on listing the versions it had before
		// the upload. On a brand-new file neither is mounted yet, so naming them
		// costs a mark in the cache and no request.
		case 'node.uploaded':
			// A root upload's `directory.all()` is the `directory` prefix, and
			// already covers all three of the file's own keys.
			return singular.parentId === null
				? [makeQueryKeys.directory.all()]
				: [
						makeQueryKeys.directory.node(singular.nodeId),
						makeQueryKeys.directory.versions(singular.nodeId),
						makeQueryKeys.directory.thumbnail(singular.nodeId),
						makeQueryKeys.directory.childrenAnySort(
							singular.parentId
						),
						makeQueryKeys.directory.folderUploads(singular.parentId)
					]
		case 'node.upload_opened':
		case 'node.upload_failed':
			return singular.parentId === null
				? [makeQueryKeys.directory.all()]
				: [
						makeQueryKeys.directory.childrenAnySort(
							singular.parentId
						),
						makeQueryKeys.directory.folderUploads(singular.parentId)
					]

		// Plus every breadcrumb: renaming a folder rewrites the path of
		// everything under it, and which nodes those are is not in the payload.
		case 'node.renamed':
			return [
				...(singular.parentId === null
					? [makeQueryKeys.directory.all()]
					: [
							makeQueryKeys.directory.childrenAnySort(
								singular.parentId
							)
						]),
				makeQueryKeys.directory.ancestorsAny(),
				makeQueryKeys.directory.favoritesAnySort()
			]

		// Two folders change and the payload names one. See the note above.
		case 'node.moved':
			return [makeQueryKeys.directory.all()]

		// A restore changes the file's content without moving it: its own
		// read (the size a download now serves), the folder it sits in (the
		// size column on its row) and its own history, all three named in
		// `events.rs`'s own doc comment for this type. `versions` is not
		// under `directory.all()`'s prefix — it is its own key, the way
		// `node` is — so a wide invalidation elsewhere never stands in for
		// this one.
		case 'node.version_restored':
			return [
				makeQueryKeys.directory.node(singular.nodeId),
				// The picture of the content it no longer serves; see `node.uploaded`.
				makeQueryKeys.directory.thumbnail(singular.nodeId),
				makeQueryKeys.directory.versions(singular.nodeId),
				...(singular.parentId === null
					? [makeQueryKeys.directory.all()]
					: [
							makeQueryKeys.directory.childrenAnySort(
								singular.parentId
							)
						])
			]

		// An earlier version was deleted (OPSSTD-9034). The current one never
		// is, so the file's size, its row and its picture are what they were:
		// the history is the one read that changed.
		case 'node.version_deleted':
			return [makeQueryKeys.directory.versions(singular.nodeId)]

		// A file's thumbnail is there now. Nothing else about the file changed —
		// not its name, its size, nor which folder it is in — so the one read
		// that is stale is the thumbnail's own. Deliberately not the listing:
		// re-reading a page of names to redraw one picture is the cost this
		// event exists to avoid.
		case 'node.thumbnail_ready':
			return [makeQueryKeys.directory.thumbnail(singular.nodeId)]

		// The node left one folder's listing and entered the bin — narrow for
		// the folder, for the reason a creation is; wide for the bin, whose
		// own listing (OPSSTD-8393) has no `parentId` to be narrow about, and
		// whose emptying preview now counts one more item.
		case 'node.trashed':
			return [
				...(singular.parentId === null
					? [makeQueryKeys.directory.all()]
					: [
							makeQueryKeys.directory.childrenAnySort(
								singular.parentId
							)
						]),
				makeQueryKeys.directory.trash(),
				makeQueryKeys.directory.trashEmptyingPreview(),
				// A favorite in the bin leaves «Speciali» and comes back with
				// the node: the list is the server's to answer.
				makeQueryKeys.directory.favoritesAnySort()
			]

		// Something came back from the bin, into the folder the payload names —
		// the one it left, or one the person chose. Narrow, like a creation,
		// for the folder it landed in; the bin page's own listing and its
		// emptying preview are always re-read, since the item just left both.
		case 'node.restored':
			return [
				...(singular.parentId === null
					? [makeQueryKeys.directory.all()]
					: [
							makeQueryKeys.directory.childrenAnySort(
								singular.parentId
							)
						]),
				makeQueryKeys.directory.trash(),
				makeQueryKeys.directory.trashEmptyingPreview(),
				// A favorite in the bin leaves «Speciali» and comes back with
				// the node: the list is the server's to answer.
				makeQueryKeys.directory.favoritesAnySort()
			]

		// Something left the bin for good. No listing showed it — it was
		// already in the bin — so the folder it came from is re-read only
		// because it is the narrowest key the payload names; the bin page's
		// own listing and its emptying preview are what this event is really
		// for (OPSSTD-8393).
		case 'node.purged':
			return [
				...(singular.parentId === null
					? [makeQueryKeys.directory.all()]
					: [
							makeQueryKeys.directory.childrenAnySort(
								singular.parentId
							)
						]),
				makeQueryKeys.directory.trash(),
				makeQueryKeys.directory.trashEmptyingPreview(),
				// A favorite in the bin leaves «Speciali» and comes back with
				// the node: the list is the server's to answer.
				makeQueryKeys.directory.favoritesAnySort()
			]

		// Visibility changed, and the payload does not say whose view of what.
		// `access.changed` names no node at all (OPSSTD-10425): the person
		// joined or left a group, or a group of theirs was deleted, so what
		// they can reach changed everywhere at once, as after a share.
		case 'share.granted':
		case 'share.revoked':
		case 'access.changed':
			return [makeQueryKeys.directory.all()]

		// A public or organization opening changed: nobody's grant moved, so
		// the people are not re-read (that would unmount an open sharing panel).
		// What moved is the node's own read (`shared`, the org opening), its
		// row in the listing that shows it (`shared` and the count), the
		// listings of its children (which take «Condiviso» from a folder above),
		// the breadcrumbs (a link visitor's path derives from the opening) and
		// the node's links. A root has no parent listing, so the row is in the
		// «Condivisi con me» root listing instead.
		case 'share.link_changed':
			return [
				makeQueryKeys.directory.node(singular.nodeId),
				makeQueryKeys.directory.childrenAnySort(singular.nodeId),
				singular.parentId === null
					? makeQueryKeys.directory.sharedWithMeAnySort()
					: makeQueryKeys.directory.childrenAnySort(
							singular.parentId
						),
				makeQueryKeys.directory.ancestorsAny(),
				makeQueryKeys.links.node(singular.nodeId)
			]

		default: {
			// Unreachable while `isKnown` and the union agree, and a compile
			// error the moment somebody adds a type to one without the other.
			const exhaustive: never = singular.type
			return exhaustive
		}
	}
}

/**
 * `trash.emptied`'s own keys: a single invalidation of the bin plus every
 * distinct origin folder the accepted tops came out of — never one per node,
 * which is the event's whole reason for existing (OPSSTD-8437).
 *
 * **Wide, and on purpose, the moment any accepted top had no parent.** A
 * `null` among `parentIds` means at least one top was a root — a personal
 * folder, or one a move had already carried out from under it — and
 * `directory.all()` is the same fallback every other event in this file takes
 * for the same fact, not a special case invented here.
 *
 * **`parentIds` may be absent rather than an empty array**, and that is not
 * this event refusing to say anything: a generic caller that builds a bare
 * `{ type: 'trash.emptied' }` — the shape every other event actually has —
 * still has to invalidate *something*, and the wide key is the only honest
 * answer to a payload this sparse. A live publication never omits it; see
 * `directory-signal.ts`.
 */
function trashEmptiedKeys(
	event: DirectoryEvent & { type: 'trash.emptied' }
): QueryKey[] {
	const { parentIds } = event
	// `.filter` with a type predicate, not a cast: it is what actually proves
	// to the compiler that nothing null survives into the narrow branch below,
	// rather than asserting it.
	const withoutRoot = parentIds?.filter((id): id is string => id !== null)

	if (withoutRoot === undefined || withoutRoot.length !== parentIds?.length) {
		return [
			makeQueryKeys.directory.all(),
			makeQueryKeys.directory.trash(),
			makeQueryKeys.directory.trashEmptyingPreview()
		]
	}

	// The server already sends distinct ids — see `events.rs`'s own
	// `BTreeSet` — but de-duplicating again here costs one `Set` and means
	// this holds even for a caller that built the payload by hand.
	return [
		...[...new Set(withoutRoot)].map((parentId) =>
			makeQueryKeys.directory.childrenAnySort(parentId)
		),
		makeQueryKeys.directory.trash(),
		makeQueryKeys.directory.trashEmptyingPreview(),
		makeQueryKeys.directory.favoritesAnySort()
	]
}
